import React, { useState, useRef, useEffect } from 'react';
import { usePresignedFileUrl } from '../attachments/usePresignedFileUrl';
import { Play } from 'lucide-react';

interface FileThumbnailProps {
  workspaceId: string;
  fileId: string;
  fileName: string;
  fileType: string;
  fileCategory?: string | undefined;
  getFileIcon: (type: string, cat?: string) => React.ReactNode;
}

export const FileThumbnail: React.FC<FileThumbnailProps> = ({
  workspaceId,
  fileId,
  fileName,
  fileType,
  fileCategory,
  getFileIcon,
}) => {
  const [loadError, setLoadError] = useState(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;

  const isImageType = fileType.startsWith('image/') || fileCategory === 'IMAGE';
  const isVideo = fileType.startsWith('video/') || fileCategory === 'VIDEO';

  // Enable presigned URL hook for images regardless of transient error state so retry works
  const presigned = usePresignedFileUrl(workspaceId, fileId, {
    enabled: isImageType,
  });

  // Clear loadError when a fresh presigned URL arrives successfully
  useEffect(() => {
    if (presigned.url && !presigned.hasError) {
      setLoadError(false);
    }
  }, [presigned.url, presigned.hasError]);

  const handleImageError = () => {
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      console.info(`[FileThumbnail] Image load error for ${fileId} — retry attempt ${retryCountRef.current}`);
      setLoadError(false);
      presigned.refresh(true);
    } else {
      setLoadError(true);
    }
  };

  if (isImageType && presigned.url && !presigned.hasError && !loadError) {
    return (
      <img
        src={presigned.url}
        alt={fileName}
        onError={handleImageError}
        className="h-full w-full object-cover group-hover:scale-105 transition duration-300"
        loading="lazy"
      />
    );
  }

  if (isVideo) {
    return (
      <div className="h-full w-full bg-slate-900 flex items-center justify-center relative group-hover:scale-105 transition duration-300">
        <div className="w-11 h-11 rounded-full bg-rose-600/90 text-white flex items-center justify-center shadow-lg">
          <Play className="h-5 w-5 fill-current ml-0.5" />
        </div>
        <span className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded text-[10px] font-mono text-white">
          VIDEO
        </span>
      </div>
    );
  }

  return (
    <div className="h-14 w-14 rounded-2xl bg-card border border-border flex items-center justify-center shadow-sm">
      {getFileIcon(fileType, fileCategory)}
    </div>
  );
};
